<template>
	<EventStageLayout :event="event" :subtitle="trans('events.stages.shopping.title')" @store="store">
		<Empty v-if="stages.length === 0"/>
		<CardGrid v-else tight>
			<Card v-for="stage in stages" :key="`shopping-stage-${stage.id}`" :title="stage.title">
				<template #badges>
					<Badge v-if="stage.active" :color="Color.Green" active>{{ trans('events.stages.active') }}</Badge>
					<Badge v-else :color="Color.Red">{{ trans('events.stages.inactive') }}</Badge>
					<Badge class="cursor-pointer" @click.stop="copyTextToClipboard(stage.id.toString())">#{{ stage.id }}</Badge>
				</template>
				<LabeledObject>
					<template #label>{{ trans('events.stages.begins_at') }}</template>
					<p>{{ dayjs(stage.begins_at).format(trans('general.dayjs.datetime')) }}</p>
				</LabeledObject>
				<LabeledObject>
					<template #label>{{ trans('events.stages.ends_at') }}</template>
					<p>{{ dayjs(stage.ends_at).format(trans('general.dayjs.datetime')) }}</p>
				</LabeledObject>
				<template #footer>
					<Link :as="SecondaryButton" :color="Color.Blue" :href="route('events.stages.shopping.edit', {event: event.id, stage: stage.id})">
						<i class="fa-solid fa-edit mr-2"/>
						{{ trans('general.edit') }}
					</Link>
					<SoftButton :color="Color.Red" @click.stop="remove(stage)">
						<i class="fa-solid fa-trash mr-2"/>
						{{ trans('general.delete') }}
					</SoftButton>
				</template>
			</Card>
		</CardGrid>
	</EventStageLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import {defineProps, type Ref, ref} from 'vue'
import {type Event} from '@/Types/Events/Event'
import Card from '@/Components/UI/Cards/Card.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import {type InertiaForm, Link} from '@inertiajs/vue3'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import EventStageLayout from '@/Layouts/EventStageLayout.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import {type ShoppingStage} from '@/Types/Events/Stages/ShoppingStage'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {copyTextToClipboard, showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	event:Event,
	stages:Array<ShoppingStage>,
}>()

const event:Ref<Event> = ref(props.event)
const working:Ref<boolean> = ref(false)
const stages:Ref<Array<ShoppingStage>> = ref(props.stages)

function store(form:InertiaForm<ShoppingStage>):void{
	if(!working.value){
		form.post(route('events.stages.shopping.store', event.value.id), {
			onStart: () => {
				working.value = true
				showInfo(trans('general.inserting'))
			},
			onSuccess: () => {
				showSuccess(trans('general.inserted'))
			},
			onFinish: () => {
				working.value = false
			},
			onError: showValidationErrors,
		})
	}
}

function remove(stage:ShoppingStage):void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('events.stages.shopping.destroy', {event: event.value.id, stage: stage.id})).then(() => {
			showSuccess(trans('general.deleted'))
			const index = stages.value.findIndex((s:ShoppingStage) => s.id === stage.id)
			if(index > -1){
				stages.value.splice(index, 1)
			}
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>